import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchDashboard, fetchRiskAnalysis, fetchMonthDetail } from '@/lib/api';
import { formatMXN, formatMXNCompact, formatPercent, formatDate } from '@/lib/formatters';
import { useConfig } from '@/lib/ConfigContext';
import { PageHeader } from '@/components/common/PageHeader';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Wallet, TrendingUp, TrendingDown, CreditCard, DollarSign,
  AlertTriangle, FileText, Home, BarChart3, HardHat, Check, Clock,
  ShieldAlert, ArrowRight,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-4 py-3 text-sm">
      <p className="font-semibold text-slate-900 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="tabular-nums" style={{ color: p.color }}>{p.name}: {formatMXN(p.value)}</p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const [data, setData]       = useState(null);
  const [risk, setRisk]       = useState(null);
  const [loading, setLoading] = useState(true);
  const { config: projectConfig } = useConfig() || {};
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      fetchDashboard().then(setData).catch(console.error),
      fetchRiskAnalysis().then(setRisk).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMeta, setDetailMeta] = useState({ mes: '', tipo: '', label: '' });

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-48" />
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6"><Skeleton className="h-72 rounded-xl" /><Skeleton className="h-72 rounded-xl" /></div>
    </div>
  );

  const k = data?.kpis || {};
  const rs = risk?.summary || {};
  const chart = data?.flujo_chart?.map(c => ({ ...c, mes: c.mes.substring(0, 3) + ' ' + c.mes.split(' ')[1]?.substring(2) })) || [];
  const flujoMensual = data?.flujo_mensual || [];

  const TIPO_LABELS = {
    ingresos_proy: 'Ingresos Proyectados',
    ingresos_real: 'Ingresos Reales',
    egresos_proy: 'Egresos Proyectados',
    egresos_real: 'Egresos Reales',
  };

  const openDetail = async (mes, tipo, mesLabel) => {
    setDetailMeta({ mes, tipo, label: `${TIPO_LABELS[tipo]} — ${mesLabel}` });
    setDetailOpen(true);
    setDetailLoading(true);
    try { setDetailData(await fetchMonthDetail(mes, tipo)); }
    catch { setDetailData(null); }
    finally { setDetailLoading(false); }
  };
  const escrituras = data?.escrituras_proximas || [];
  const obra = data?.obra || {};
  const cumplimiento = rs.cumplimiento_global || 0;
  const overdueCount = rs.risk_alto || 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle={projectConfig?.proyecto_nombre || data?.proyecto || ''} />

      {/* ── KPIs: Comparativo Mensual ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
        <KPICard label="Ing. Proy." value={formatMXNCompact(k.ingresos_proy_mes)} icon={Clock} subtitle={k.mes_label} compact testId="kpi-ing-proy" />
        <KPICard label="Ing. Reales" value={formatMXNCompact(k.ingresos_real_mes)} icon={TrendingUp} subtitle={k.mes_label} compact testId="kpi-ing-real" />
        <KPICard label="Eg. Proy." value={formatMXNCompact(k.egresos_proy_mes)} icon={TrendingDown} subtitle={k.mes_label} compact testId="kpi-eg-proy" />
        <KPICard label="Eg. Reales" value={formatMXNCompact(k.egresos_real_mes)} icon={TrendingDown} subtitle={k.mes_label} compact testId="kpi-eg-real" />
        <KPICard label="Caja Proy." value={formatMXNCompact(k.caja_proy_mes)} icon={Wallet} subtitle={k.mes_label} className={(k.caja_proy_mes || 0) < 0 ? 'border-red-200' : ''} compact testId="kpi-caja-proy" />
        <KPICard label="Caja Real" value={formatMXNCompact(k.caja_real_ultimo || k.caja_real_mes)} icon={Wallet} subtitle={k.ultimo_mes_real || k.mes_label} className={(k.caja_real_mes || 0) < 0 ? 'border-red-200' : 'border-emerald-200'} compact testId="kpi-caja-real" />
        <KPICard label="Cumplimiento" value={`${k.cumplimiento_mes || 0}%`} icon={(k.cumplimiento_mes || 0) >= 80 ? Check : AlertTriangle} subtitle={k.mes_label} className={(k.cumplimiento_mes || 0) < 50 ? 'border-red-200' : (k.cumplimiento_mes || 0) < 80 ? 'border-amber-200' : 'border-emerald-200'} compact testId="kpi-cumplimiento" />
        <KPICard label="Crédito" value={formatMXNCompact(k.credito_actual)} icon={CreditCard} subtitle={k.mes_label} compact testId="kpi-credito" />
      </div>

      {/* ── Risk Alert ── */}
      {risk && (risk.summary.risk_alto > 0 || risk.summary.risk_medio > 0) && (
        <Card
          className="rounded-xl border border-amber-200 bg-amber-50/50 cursor-pointer hover:bg-amber-50/80 transition-colors"
          onClick={() => navigate('/validacion')}
          data-testid="risk-alert-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                <ShieldAlert className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800">
                  {risk.summary.risk_alto + risk.summary.risk_medio} vivienda{risk.summary.risk_alto + risk.summary.risk_medio !== 1 ? 's' : ''} en riesgo operativo
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {formatMXNCompact(risk.summary.income_at_risk)} en ingresos en riesgo · {risk.summary.liberation_issues} inconsistencias en liberaciones
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-0.5">Flujo ajustado</p>
                <p className={`text-lg font-bold tabular-nums ${risk.summary.flujo_ajustado < 0 ? 'text-red-600' : 'text-slate-700'}`} style={{ fontFamily: 'Space Grotesk' }}>
                  {formatMXNCompact(risk.summary.flujo_ajustado)}
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Charts Row ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Gráfica Caja */}
        <Card className="rounded-xl border bg-white" data-testid="chart-caja">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Wallet className="h-4 w-4 text-[#F05A28]" /> Evolución de Caja
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={0.6} />
                <XAxis dataKey="mes" tick={{ fontSize: 9, fill: '#64748B' }} interval={0} angle={-45} textAnchor="end" height={50} />
                <YAxis tickFormatter={v => `$${(v / 1e6).toFixed(0)}M`} tick={{ fontSize: 10, fill: '#64748B' }} />
                <RechartsTooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="4 4" strokeOpacity={0.4} />
                <Line type="monotone" dataKey="proy_caja" name="Caja Proyectada" stroke="#94A3B8" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2, fill: '#94A3B8' }} />
                <Line type="monotone" dataKey="real_caja" name="Caja Real" stroke="#F05A28" strokeWidth={2.5} dot={{ r: 2, fill: '#F05A28' }} />
                <Line type="monotone" dataKey="credito" name="Crédito" stroke="#162A45" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2, fill: '#162A45' }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Ingresos vs Egresos */}
        <Card className="rounded-xl border bg-white" data-testid="chart-ingresos-egresos">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-[#F05A28]" /> Ingresos: Proyectados vs Reales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={0.6} />
                <XAxis dataKey="mes" tick={{ fontSize: 9, fill: '#64748B' }} interval={0} angle={-45} textAnchor="end" height={50} />
                <YAxis tickFormatter={v => `$${(v / 1e6).toFixed(0)}M`} tick={{ fontSize: 10, fill: '#64748B' }} />
                <RechartsTooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="proy_ingresos" name="Ing. Proyectados" fill="#94A3B8" fillOpacity={0.8} radius={[4, 4, 0, 0]} />
                <Bar dataKey="real_ingresos" name="Ing. Reales" fill="#22C55E" fillOpacity={0.9} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* ── Bottom Widgets Row ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Escrituras Próximas */}
        <Card className="rounded-xl border bg-white overflow-hidden" data-testid="widget-escrituras">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-500" />
              Escrituras Próximas
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] ml-1">{escrituras.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {escrituras.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Sin escrituras pendientes</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-slate-500">Propiedad</TableHead>
                    <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-slate-500">Estatus</TableHead>
                    <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-slate-500 text-right">Precio</TableHead>
                    <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-slate-500 text-right">Liberación</TableHead>
                    <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-slate-500 text-right">Pendiente</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {escrituras.map(e => (
                    <TableRow key={e.id} className="table-row-hover">
                      <TableCell className="py-2 px-3 text-xs font-medium">{e.name}</TableCell>
                      <TableCell className="py-2 px-3">
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          Apartada
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 px-3 text-right tabular-nums text-xs">{formatMXNCompact(e.sale_price)}</TableCell>
                      <TableCell className="py-2 px-3 text-right tabular-nums text-xs text-blue-600 font-medium">{formatMXNCompact(e.release_amount)}</TableCell>
                      <TableCell className="py-2 px-3 text-right tabular-nums text-xs text-amber-600 font-medium">{formatMXNCompact(e.saldo_pendiente)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Flujo Mensual */}
        <Card className="rounded-xl border bg-white overflow-hidden" data-testid="widget-flujo-mensual">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#F05A28]" />
              Flujo Mensual
              <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px] ml-1">{flujoMensual.length} meses</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {flujoMensual.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Sin datos de flujo</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="text-[10px] uppercase py-2 px-2 font-semibold text-slate-500 whitespace-nowrap">Mes</TableHead>
                    <TableHead className="text-[10px] uppercase py-2 px-2 font-semibold text-slate-400 text-right whitespace-nowrap">Ing. Proy.</TableHead>
                    <TableHead className="text-[10px] uppercase py-2 px-2 font-semibold text-[#F05A28] text-right whitespace-nowrap">Ing. Real</TableHead>
                    <TableHead className="text-[10px] uppercase py-2 px-2 font-semibold text-slate-400 text-right whitespace-nowrap">Eg. Proy.</TableHead>
                    <TableHead className="text-[10px] uppercase py-2 px-2 font-semibold text-[#F05A28] text-right whitespace-nowrap">Eg. Real</TableHead>
                    <TableHead className="text-[10px] uppercase py-2 px-2 font-semibold text-slate-500 text-right whitespace-nowrap">Caja Proy.</TableHead>
                    <TableHead className="text-[10px] uppercase py-2 px-2 font-semibold text-[#F05A28] text-right whitespace-nowrap">Caja Real</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flujoMensual.map(m => {
                    const cajaNeg = (m.proy_caja || 0) < 0;
                    const cl = "py-2 px-2 text-right tabular-nums text-xs cursor-pointer hover:bg-slate-50 transition-colors";
                    return (
                      <TableRow key={m.mes} className={`${cajaNeg ? 'bg-red-50/40' : ''}`}>
                        <TableCell className="py-2 px-2 text-xs font-medium whitespace-nowrap">{m.mes_label}</TableCell>
                        <TableCell className={cl} onClick={() => openDetail(m.mes, 'ingresos_proy', m.mes_label)}>{(m.proy_ingresos || 0) > 0 ? formatMXNCompact(m.proy_ingresos) : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className={`${cl} text-[#F05A28] font-medium`} onClick={() => openDetail(m.mes, 'ingresos_real', m.mes_label)}>{(m.real_ingresos || 0) > 0 ? formatMXNCompact(m.real_ingresos) : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className={cl} onClick={() => openDetail(m.mes, 'egresos_proy', m.mes_label)}>{formatMXNCompact(m.proy_egresos)}</TableCell>
                        <TableCell className={`${cl} text-[#F05A28]`} onClick={() => openDetail(m.mes, 'egresos_real', m.mes_label)}>{formatMXNCompact(m.real_egresos)}</TableCell>
                        <TableCell className={`py-2 px-2 text-right tabular-nums text-xs font-bold ${cajaNeg ? 'value-negative' : ''}`}>{formatMXNCompact(m.proy_caja)}</TableCell>
                        <TableCell className={`py-2 px-2 text-right tabular-nums text-xs font-bold text-[#F05A28] ${(m.real_caja || 0) < 0 ? 'value-negative' : ''}`}>{formatMXNCompact(m.real_caja)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Sales Summary ── */}
      <Card className="rounded-xl border bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Home className="h-4 w-4 text-[#F05A28]" /> Avance de Ventas — {formatPercent(k.avance_ventas)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { v: k.propiedades_vendidas, l: 'Vendidas', color: 'text-slate-900' },
              { v: k.propiedades_apartadas + (k.propiedades_escrituracion || 0), l: 'Apartadas', color: 'text-amber-700' },
              { v: k.propiedades_disponibles, l: 'Disponibles', color: 'text-emerald-700' },
            ].map((item, i) => (
              <div key={i} className="text-center p-3 rounded-lg bg-slate-50">
                <p className={`text-2xl font-semibold tabular-nums ${item.color}`}>{item.v || 0}</p>
                <p className="text-xs text-slate-500 mt-1">{item.l}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ══════════════ AVANCE DE OBRA ══════════════ */}
      {obra.con_plan > 0 && (
        <>
          <div className="h-px bg-slate-200 my-2" />

          {/* Obra KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KPICard label="Avance Proyectado" value={`${obra.avg_proyectado}%`} icon={Clock} compact testId="kpi-obra-proy" />
            <KPICard label="Avance Real" value={`${obra.avg_real}%`} icon={HardHat} compact testId="kpi-obra-real" />
            <KPICard label="Atrasadas" value={obra.atrasadas} icon={AlertTriangle} className={obra.atrasadas > 0 ? 'border-red-200' : ''} compact testId="kpi-obra-atrasadas" />
            <KPICard label="Adelantadas" value={obra.adelantadas} icon={Check} className={obra.adelantadas > 0 ? 'border-emerald-200' : ''} compact testId="kpi-obra-adelantadas" />
          </div>

          {/* Obra chart: projected vs real by property */}
          {obra.por_vivienda?.length > 0 && (
            <Card className="rounded-xl border bg-white" data-testid="chart-avance-obra">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <HardHat className="h-4 w-4 text-[#F05A28]" /> Avance por Vivienda — Proyectado vs Real
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={obra.por_vivienda.map(v => ({ name: `L${v.lot} M${v.block}`, proy: v.projected, real: v.actual }))} >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={0.6} />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#64748B' }} interval={0} />
                    <YAxis tick={{ fontSize: 9, fill: '#64748B' }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                    <RechartsTooltip formatter={(v) => `${v}%`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="proy" name="Proyectado" fill="#94A3B8" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="real" name="Real" fill="#F05A28" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Obra tables row */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Top atrasos */}
            <Card className="rounded-xl border bg-white overflow-hidden" data-testid="widget-atrasos">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" /> Mayor Atraso
                  <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-[10px] ml-1">{obra.atrasadas}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {obra.top_atrasos?.length === 0 ? (
                  <div className="text-center py-6"><p className="text-xs text-emerald-600 font-medium">Sin atrasos</p></div>
                ) : (
                  <Table>
                    <TableHeader><TableRow className="bg-red-50/30">
                      <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-red-500">Vivienda</TableHead>
                      <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-red-500 text-right">Proy.</TableHead>
                      <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-red-500 text-right">Real</TableHead>
                      <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-red-500 text-right">Var.</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {obra.top_atrasos?.map(w => (
                        <TableRow key={w.property_id} className="table-row-hover">
                          <TableCell className="py-2 px-3 text-xs font-medium">L{w.lot} Mz{w.block}</TableCell>
                          <TableCell className="py-2 px-3 text-right text-xs tabular-nums">{w.projected}%</TableCell>
                          <TableCell className="py-2 px-3 text-right text-xs tabular-nums font-bold text-[#F05A28]">{w.actual}%</TableCell>
                          <TableCell className="py-2 px-3 text-right text-xs tabular-nums font-medium text-red-500">{w.variance}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Últimas actualizaciones */}
            <Card className="rounded-xl border bg-white overflow-hidden" data-testid="widget-recientes">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" /> Últimas Actualizaciones
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {obra.recientes?.length === 0 ? (
                  <div className="text-center py-6"><p className="text-xs text-slate-400">Sin actualizaciones</p></div>
                ) : (
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50/80">
                      <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-slate-500">Vivienda</TableHead>
                      <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-slate-500 text-right">Real</TableHead>
                      <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-slate-500">Fecha</TableHead>
                      <TableHead className="text-[10px] uppercase py-2 px-3 font-semibold text-slate-500">Estatus</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {obra.recientes?.map(w => (
                        <TableRow key={w.property_id} className="table-row-hover">
                          <TableCell className="py-2 px-3 text-xs font-medium">L{w.lot} Mz{w.block}</TableCell>
                          <TableCell className="py-2 px-3 text-right text-xs tabular-nums font-bold">{w.actual}%</TableCell>
                          <TableCell className="py-2 px-3 text-[10px] tabular-nums text-slate-500">{formatDate(w.last_update)}</TableCell>
                          <TableCell className="py-2 px-3">
                            <Badge variant="outline" className={`text-[9px] ${w.status === 'atrasada' ? 'bg-red-50 text-red-600 border-red-200' : w.status === 'adelantada' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
                              {w.status === 'atrasada' ? 'Atrasada' : w.status === 'adelantada' ? 'Adelantada' : 'En línea'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={v => { if (!v) { setDetailOpen(false); setDetailData(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm">{detailMeta.label}</DialogTitle>
          </DialogHeader>
          {detailLoading ? <Skeleton className="h-40" /> : detailData ? (
            <div className="space-y-3 py-2">
              <div className="flex justify-between items-center bg-slate-50 rounded-lg p-3">
                <span className="text-xs text-slate-500">{detailData.count} {detailMeta.tipo.startsWith('ingresos') ? 'pagos' : 'conceptos'}</span>
                <span className="text-[15px] font-bold tabular-nums text-[#F05A28]">{formatMXN(detailData.total)}</span>
              </div>
              {detailData.items.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      {detailMeta.tipo.startsWith('ingresos') ? (
                        <>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2">Vivienda</TableHead>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2">Cliente</TableHead>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2">Tipo</TableHead>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2 text-right">Monto</TableHead>
                        </>
                      ) : (
                        <>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2">Categoría</TableHead>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2 text-right">Monto</TableHead>
                        </>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailData.items.map((item, i) => (
                      <TableRow key={i}>
                        {detailMeta.tipo.startsWith('ingresos') ? (
                          <>
                            <TableCell className="py-1.5 px-2 text-[11px] font-medium">{item.vivienda}</TableCell>
                            <TableCell className="py-1.5 px-2 text-[11px] text-slate-500 truncate max-w-[120px]">{item.cliente}</TableCell>
                            <TableCell className="py-1.5 px-2"><Badge variant="outline" className="text-[8px]">{item.tipo_pago === 'anticipo' ? 'Anticipo' : item.tipo_pago === 'finiquito' ? 'Finiquito' : 'Intermedio'}</Badge></TableCell>
                            <TableCell className="py-1.5 px-2 text-right text-[11px] tabular-nums font-semibold">{formatMXN(item.monto)}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="py-1.5 px-2 text-[11px] font-medium">{item.categoria}</TableCell>
                            <TableCell className="py-1.5 px-2 text-right text-[11px] tabular-nums font-semibold">{formatMXN(item.monto)}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <p className="text-xs text-slate-400 text-center py-4">Sin registros para este periodo</p>}
            </div>
          ) : <p className="text-xs text-slate-400 text-center py-4">Error al cargar detalle</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
